// Trade configurations for Anne Flinn Advertising — Home Services landing pages.
// Each slug becomes a route: /home-services/<slug>

export const trades = {
  hvac: {
    slug: "hvac",
    name: "HVAC Contractors",
    shortName: "HVAC",
    icon: "Snowflake",
    accent: "from-sky-400 to-amber-300",
    heroEyebrow: "FOR HVAC CONTRACTORS",
    heroHeadline: "When the AC dies at 9pm, be the truck they picture.",
    heroSub:
      "Targeted streaming + mobile display advertising for HVAC companies serving Central Virginia. Be the brand homeowners already recognize before they ever need to make the call.",
    painPoints: [
      "Replacement and after-hours calls go to the brand the homeowner already recognizes",
      "Spring tune-up season is a 6-week window, miss it and the calendar stays empty",
      "Competing against national franchises with seven-figure ad budgets",
    ],
    seasonalTriggers: [
      { season: "Heat waves", trigger: "Heat index > 90°F activates AC repair + install ads" },
      { season: "Cold snaps", trigger: "Freeze warnings activate furnace + heat pump ads" },
      { season: "Spring + Fall", trigger: "Tune-up promo ads run before peak season hits" },
    ],
    packageExamples: {
      topOfMind:
        "Run 15- or 30-second video spots on HGTV, ESPN, Weather Channel, and local news. Build the brand homeowners picture first when they need HVAC.",
      callNow:
        "Target neighborhoods with 15+ year old systems via PMAX zip and radius targeting. Swap creative for tune-up promos and seasonal offers.",
      marketDominator:
        "Brand awareness on the big screen + always-on display in the palm of their hand. Best for full-service shops competing with One Hour Heating & Air, ARS, or other national brands.",
    },
    testimonial: null,
  },

  plumbers: {
    slug: "plumbers",
    name: "Plumbers",
    shortName: "Plumbing",
    icon: "Wrench",
    accent: "from-blue-500 to-amber-300",
    heroEyebrow: "FOR PLUMBING CONTRACTORS",
    heroHeadline: "Be the first name they remember when the pipe bursts.",
    heroSub:
      "Streaming + mobile display advertising packages built for plumbing companies. Build the brand recognition that wins water heater replacements, bathroom remodels, and after-hours calls, all under one budget.",
    painPoints: [
      "Burst pipes don't wait, whoever the homeowner remembers wins the call",
      "Water heater replacements are a $3K–$8K job that goes to whoever the homeowner remembers",
      "Yelp, Angi, and Thumbtack take a cut of every lead, your own ads don't",
    ],
    seasonalTriggers: [
      { season: "Cold snaps", trigger: "Freeze alerts activate burst-pipe + frozen-line creative" },
      { season: "Storm season", trigger: "Sump pump and drain backup ads run after heavy rain" },
      { season: "Year-round", trigger: "Water heater replacement ads targeted to 10+ year old homes" },
    ],
    packageExamples: {
      topOfMind:
        "Brand-build on HGTV, DIY Network, and local news so homeowners save your number before the pipe bursts.",
      callNow:
        "Target Home Depot, Lowe's, and competitor service areas with radius-based PMAX campaigns. Ads keep your brand top of mind for replacement work and after-hours calls.",
      marketDominator:
        "Own planned replacement work and after-hours brand recall. Streaming for the big screen, mobile display for the rest of their day.",
    },
    testimonial: null,
  },

  electricians: {
    slug: "electricians",
    name: "Electricians",
    shortName: "Electrical",
    icon: "Zap",
    accent: "from-amber-400 to-blue-500",
    heroEyebrow: "FOR ELECTRICAL CONTRACTORS",
    heroHeadline: "From panel upgrades to EV chargers, be the brand they know.",
    heroSub:
      "Streaming + PMAX advertising for residential electricians in Central Virginia. Position yourself as the trusted local pro for service work, panel upgrades, and EV installs.",
    painPoints: [
      "Panel upgrades and rewires are high-ticket jobs that go to the most trusted brand in the market",
      "EV charger installs are exploding, most electricians aren't marketing to that buyer yet",
      "Generator installs are seasonal but margin-rich, timing matters",
    ],
    seasonalTriggers: [
      { season: "Storm season", trigger: "Power outages activate generator install ads" },
      { season: "Year-round", trigger: "EV charger ads target households with new EV registrations" },
      { season: "Holiday season", trigger: "Lighting + outlet upgrade ads run Oct–Dec" },
    ],
    packageExamples: {
      topOfMind:
        "Position yourself on HGTV, DIY, ESPN, and local news. Homeowners hire the electrician they recognize.",
      callNow:
        "Target neighborhoods with new EVs, older panels (homes 25+ years), and after-storm zones.",
      marketDominator:
        "Capture both planned work (panels, EV, generators) and reactive service calls with a full-funnel push.",
    },
    testimonial: null,
  },

  roofers: {
    slug: "roofers",
    name: "Roofers",
    shortName: "Roofing",
    icon: "Home",
    accent: "from-amber-400 to-red-500",
    heroEyebrow: "FOR ROOFING CONTRACTORS",
    heroHeadline: "Storm hits at noon. Your ads run by 5pm.",
    heroSub:
      "Streaming + PMAX advertising packages for roofing contractors. Be first on the door after every hailstorm, every wind event, every leak.",
    painPoints: [
      "Storm chasers from out of state flood the market within 24 hours of every hailstorm",
      "Roof replacements are $15K–$40K jobs, whoever the homeowner sees first usually wins",
      "Insurance work has a tight window, speed-to-market is everything",
    ],
    seasonalTriggers: [
      { season: "Storm season", trigger: "Hail + wind events trigger ads in affected ZIP codes within hours" },
      { season: "Spring", trigger: "Annual inspection + 'is your roof storm-ready' messaging" },
      { season: "Fall", trigger: "Pre-winter inspection and gutter ads" },
    ],
    packageExamples: {
      topOfMind:
        "Build your brand year-round on HGTV, Weather Channel, ESPN, and local news so homeowners know you before the storm.",
      callNow:
        "Target storm-damaged ZIPs within hours via PMAX. Swap creative to 'Free Storm Inspection' the moment NOAA issues a report.",
      marketDominator:
        "Own your market before, during, and after every storm. Brand + lead-gen running in tandem.",
    },
    testimonial: null,
  },

  "lawn-landscape": {
    slug: "lawn-landscape",
    name: "Lawn & Landscaping",
    shortName: "Lawn & Landscape",
    icon: "Leaf",
    accent: "from-emerald-500 to-amber-300",
    heroEyebrow: "FOR LAWN & LANDSCAPING PROS",
    heroHeadline: "Lock in the contracts before mowing season starts.",
    heroSub:
      "Streaming + PMAX advertising for lawn care, landscaping, hardscape, and tree services. Fill your route before April, and keep adding upgrades all season.",
    painPoints: [
      "Contracts are locked in Feb–April, if homeowners haven't seen you by March, you lose the season",
      "One-time mows are a race to the bottom, recurring contracts and design/install work are where the margin lives",
      "Competing against the kid with a mower AND the national franchises",
    ],
    seasonalTriggers: [
      { season: "Late winter", trigger: "'Lock in your spring contract' ads run Feb–March" },
      { season: "Spring", trigger: "Mulch, aeration, and landscape design ads" },
      { season: "Summer", trigger: "Mosquito treatment, irrigation, and hardscape upgrade ads" },
      { season: "Fall", trigger: "Leaf cleanup, overseeding, and tree work ads" },
    ],
    packageExamples: {
      topOfMind:
        "Build seasonal brand recall on HGTV, DIY, and local news. Homeowners hire the landscaper they've seen all year.",
      callNow:
        "Zip-target neighborhoods (high lot value, mature homes). Swap creative weekly with season-specific offers.",
      marketDominator:
        "Year-round visibility + season-triggered offers. Best for design/build firms wanting to dominate their service area.",
    },
    testimonial: null,
  },

  "pest-control": {
    slug: "pest-control",
    name: "Pest Control",
    shortName: "Pest Control",
    icon: "Bug",
    accent: "from-lime-500 to-amber-400",
    heroEyebrow: "FOR PEST CONTROL COMPANIES",
    heroHeadline: "When the ants come marching, you're the first call.",
    heroSub:
      "Streaming + PMAX advertising for pest control and mosquito-treatment companies. Lock in recurring contracts and respond fast to seasonal infestations.",
    painPoints: [
      "Pest issues are seasonal AND emotional, homeowners want it gone today",
      "Recurring contracts are the lifeblood, one-time treatments don't scale",
      "Mosquito season is short. Termite leads are golden. You need to be visible for both",
    ],
    seasonalTriggers: [
      { season: "Spring", trigger: "Termite swarm season + ant invasion ads" },
      { season: "Summer", trigger: "Mosquito treatment ads targeted to outdoor-living neighborhoods" },
      { season: "Fall", trigger: "Rodent + spider prevention ads as temps drop" },
    ],
    packageExamples: {
      topOfMind:
        "Build trust through brand-building on HGTV, ESPN, and local news so homeowners think of you first.",
      callNow:
        "Target neighborhoods with pools, outdoor kitchens, or recent pest complaints via PMAX hyper-local targeting.",
      marketDominator:
        "Own residential pest in your market. Streaming brand-build + PMAX lead-gen for recurring contracts AND one-off jobs.",
    },
    testimonial: null,
  },
};

export const tradeList = Object.values(trades);
